import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Card, CardContent, CircularProgress, Container, Typography } from '@mui/material';
import PublicSiteHeader from '@components/layout/PublicSiteHeader';
import SiteFooter from '@components/layout/SiteFooter';
import PublicAcademyProfilePage from '@pages/PublicAcademyProfilePage';
import PublicPlayerProfilePage from '@pages/PublicPlayerProfilePage';
import { brandColors } from '@config/brandTokens';

const PublicProfileDispatchPage: React.FC = () => {
  const { slug = '' } = useParams();
  const [ownerType, setOwnerType] = useState<'player' | 'academy' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!slug) {
        setError('Profile URL is required');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`/api/public/profile/${encodeURIComponent(slug)}`);
        const data = await response.json();
        if (!response.ok || !data?.success) {
          throw new Error(data?.message || 'Public profile not found');
        }
        const type = data.data?.ownerType;
        if (type !== 'player' && type !== 'academy') {
          throw new Error('Public profile not found');
        }
        setOwnerType(type);
      } catch (err: any) {
        setOwnerType(null);
        setError(err?.message || 'Public profile not found');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [slug]);

  if (ownerType === 'academy') return <PublicAcademyProfilePage />;
  if (ownerType === 'player') return <PublicPlayerProfilePage />;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: brandColors.ice, display: 'flex', flexDirection: 'column' }}>
      <PublicSiteHeader />
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center', flex: 1 }}>
        {loading ? (
          <>
            <CircularProgress />
            <Typography sx={{ mt: 1.5, color: brandColors.slate }}>Loading public profile...</Typography>
          </>
        ) : (
          <Card>
            <CardContent>
              <Typography color="error" sx={{ fontWeight: 700 }}>
                {error || 'Public profile not found'}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Container>
      <SiteFooter />
    </Box>
  );
};

export default PublicProfileDispatchPage;
